/**
 * The offer drawer's redemption ledger and the frozen editor's "Clone as new offer"
 * (docs/05-admin/25 §2 and §3.2 item 7).
 *
 * **Built from the spec, not a design.** The design draws neither; the owner ruled on 2026-10-26
 * that design-less screens are built from the docs with the console's own components.
 *
 * The ledger is the server's `GET /v1/admin/offers/{id}/redemptions`, paged there. Its states are
 * the schema's three — `applied` holds budget, `consumed` spent it, `released` returned it; the
 * doc's `reversed` never existed (`CouponRedemptionStatus`). The row carries no device
 * fingerprint, so the doc's "fingerprint presence" column is not drawn; the farming lens has it.
 *
 * A clone copies the deal exactly under a new slug and lands as a draft, which nobody has
 * redeemed, so every term is editable again there. That is the §2 rule: a changed deal is a new
 * offer, never an edit to one a devotee already saw.
 */
import { useState } from 'react';
import { formatInr } from '../lib/money';
import { Chip, StatusChip } from '../components/primitives';
import { Input } from '../components/forms';
import { DataTable, Pager } from '../components/data';
import { EmptyState, ErrorState, LoadingState } from '../components/feedback';
import { ConfirmAction, Money, ScopeAction, Section } from '../components/kit';
import { ApiError, errorMessage } from '../lib/api/client';
import { useConsole } from '../app/console';
import { useCreateOffer, useOfferRedemptions } from '../lib/api/hooks/offers';
import type { OfferCreateIn, OfferRow } from '../lib/api/hooks/offers';

export const REDEMPTION_STATES = [
  { key: 'applied', label: 'Holding budget', tone: 'warn' },
  { key: 'consumed', label: 'Spent', tone: 'success' },
  { key: 'released', label: 'Released', tone: 'neutral' },
] as const;

const PAGE_SIZE = 50;

function stateOf(status: string) {
  return REDEMPTION_STATES.find((s) => s.key === status);
}

export function OfferRedemptions({ offerId }: { offerId: number }) {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<string | undefined>(undefined);
  const ledger = useOfferRedemptions(offerId, { page, page_size: PAGE_SIZE, status });

  const filter = (next: string | undefined) => {
    setStatus(next);
    setPage(1);
  };

  return (
    <Section title="Redemption ledger" note="Every discount this campaign gave, newest first" pad={false}>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', padding: 'var(--sp-sm) var(--sp-md)' }}>
        <Chip variant="filter" label="All" selected={status === undefined} onClick={() => filter(undefined)} />
        {REDEMPTION_STATES.map((s) => (
          <Chip key={s.key} variant="filter" label={s.label} selected={status === s.key} onClick={() => filter(s.key)} />
        ))}
      </div>
      {ledger.isPending && <LoadingState rows={2} message="Reading redemptions…" />}
      {ledger.isError && (
        <ErrorState inline title="The ledger is unavailable" cause={errorMessage(ledger.error)} />
      )}
      {ledger.data && ledger.data.items.length === 0 && (
        <EmptyState
          inline
          icon="percent"
          title="No redemptions"
          body={status === undefined ? 'Nobody has used this offer yet.' : 'None in this state.'}
        />
      )}
      {ledger.data && ledger.data.items.length > 0 && (
        <DataTable
          density="compact"
          columns={[
            { key: 'booking', label: 'Booking', mono: true },
            { key: 'code', label: 'Code', mono: true },
            { key: 'user', label: 'Devotee', mono: true },
            { key: 'discount', label: 'Discount', align: 'right' },
            { key: 'state', label: 'State' },
            { key: 'when', label: 'When' },
          ]}
          rows={ledger.data.items.map((r) => {
            const state = stateOf(r.status);
            return {
              id: r.id,
              booking: r.booking_code,
              code: r.coupon_code ?? 'auto',
              user: `#${r.user_id}`,
              discount: <Money paise={r.discount_paise} />,
              state: <StatusChip tone={state?.tone ?? 'neutral'} label={state?.label ?? r.status} />,
              when: new Date(r.created_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }),
            };
          })}
        />
      )}
      {ledger.data && (
        <Pager page={page} pageSize={PAGE_SIZE} total={ledger.data.total} onPage={setPage} />
      )}
    </Section>
  );
}

/** The deal exactly as it stands, under a new slug and internal name. */
export function cloneInput(offer: OfferRow, slug: string): OfferCreateIn {
  return {
    slug,
    name: `${offer.name} (copy)`,
    title: offer.title,
    terms: offer.terms,
    offer_type: offer.offer_type,
    discount_kind: offer.discount_kind,
    discount_value: offer.discount_value,
    max_discount_paise: offer.max_discount_paise,
    min_order_paise: offer.min_order_paise,
    first_booking_only: offer.first_booking_only,
    per_user_limit: offer.per_user_limit,
    total_redemption_limit: offer.total_redemption_limit,
    budget_paise: offer.budget_paise,
    /* The effective lists, not the single columns: a legacy offer's column is folded into its
       list, and the server refuses a column and its list together. */
    city_ids: offer.city_ids,
    puja_ids: offer.puja_ids,
    puja_category_id: offer.puja_category_id,
    starts_at: offer.starts_at,
    ends_at: offer.ends_at,
  };
}

export function CloneOffer({ offer }: { offer: OfferRow }) {
  const { scope, toast } = useConsole();
  const create = useCreateOffer();
  const [slug, setSlug] = useState(`${offer.slug}-2`);
  const [seconder, setSeconder] = useState('');
  const [confirming, setConfirming] = useState(false);
  const needsSecond = create.error instanceof ApiError && create.error.code === 'second_approval_required';
  const seconderId = Number(seconder);

  const submit = () =>
    create.mutate(
      {
        ...cloneInput(offer, slug.trim()),
        ...(needsSecond ? { second_approver_admin_id: seconderId } : {}),
      },
      { onSuccess: (made) => toast({ tone: 'success', message: `Draft ${made.slug} created — edit its terms there` }) },
    );

  return (
    <div style={{ display: 'grid', gap: 'var(--sp-sm)' }}>
      <Input density="admin" label="New offer slug" value={slug} onChange={setSlug} />
      {needsSecond && (
        <Input
          density="admin"
          label="Seconded by (admin id)"
          value={seconder}
          onChange={setSeconder}
          helper="This budget needs a second approver"
        />
      )}
      {create.error && !needsSecond && <ErrorState inline title="Not cloned" cause={errorMessage(create.error)} />}
      <ScopeAction
        scope={scope}
        need="growth"
        size="sm"
        disabled={
          slug.trim() === '' || create.isPending || (needsSecond && !(Number.isInteger(seconderId) && seconderId > 0))
        }
        onClick={() => setConfirming(true)}
      >
        Clone as new offer
      </ScopeAction>
      {confirming && (
        // The copy carries the original's whole budget, so creating it is typed, not clicked.
        <ConfirmAction
          open
          title={`Create draft ${slug.trim()} as a copy of ${offer.slug}?`}
          phrase="CLONE"
          consequences={[
            offer.budget_paise === null
              ? 'The copy has no budget cap, like the original.'
              : `The copy carries its own budget of ${formatInr(offer.budget_paise)}, separate from the original's.`,
            'It lands as a draft: nobody sees it until it is activated, and every term stays editable there.',
          ]}
          confirmLabel="Create draft"
          onCancel={() => setConfirming(false)}
          onConfirm={() => {
            submit();
            setConfirming(false);
          }}
        />
      )}
    </div>
  );
}
